<script setup lang="ts">
/**
 * 站台指標**顯示定義**的編輯對話框（批112）。
 *
 * 表單範式照抄 `ProductsView.vue` 的「行為參數（policies）」區塊：代號在編輯時鎖住、
 * 中文顯示名、型別下拉、值域欄位隨型別切換、說明、停用勾選、`valid` computed 控制
 * 確定鈕。差別是那邊編的是**產品宣告的一個 entry**（存在草稿陣列裡，整包送出），
 * 這邊每一列自己就是一列 DB 資料，所以直接打 API。
 *
 * ★ 抽成元件是為了 `DeploymentsView` 的「未定義指標 → ＋ 建立顯示定義」——那顆小鈕要開的
 *   就是這一個 modal（`product`／`key` 預填唯讀），不該把整份表單再抄一次。
 * ★ **不用** `PolicyEditor.vue`／`QuotaEditor.vue`：那兩支是「依宣告 render 表單、讓人填值」，
 *   這裡是**編輯宣告本身**，方向相反。
 */
import { computed, ref, watch } from 'vue'

import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { MetricDefinition } from '@/api/ls'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { useDialogsStore } from '@/stores/dialogs'

const props = defineProps<{
  open: boolean
  /** 非 null＝編輯既有列（`product`／`key` 鎖住）。 */
  row?: MetricDefinition | null
  /** 新增時預填並鎖住的產品／指標鍵（從部署頁的「未定義指標」進來時用）。 */
  presetProduct?: string | null
  presetKey?: string | null
  /** 產品下拉的選項（`{key, name}`）；空陣列＝退化成純文字輸入。 */
  products?: { key: string; name: string }[]
}>()
const emit = defineEmits<{ close: []; saved: [MetricDefinition] }>()

const dialogs = useDialogsStore()
const saving = ref(false)

interface Draft {
  product: string
  key: string
  label: string
  unit: string
  value_format: 'integer' | 'decimal' | 'percent'
  decimals: number | null
  sort_order: number | null
  hint: string
  hidden: boolean
}

function blank(): Draft {
  return {
    product: props.presetProduct ?? props.products?.[0]?.key ?? '',
    key: props.presetKey ?? '',
    label: '',
    unit: '',
    value_format: 'decimal',
    decimals: 2,
    sort_order: 0,
    hint: '',
    hidden: false,
  }
}

const draft = ref<Draft>(blank())

/** 開窗當下才重置——`UiModal` 的內容是 v-if 掛載的，關窗即卸載，狀態不會殘留。 */
watch(
  () => props.open,
  (o) => {
    if (!o) return
    const r = props.row
    draft.value = r
      ? {
          product: r.product,
          key: r.key,
          label: r.label,
          unit: r.unit ?? '',
          value_format: r.value_format,
          decimals: r.decimals,
          sort_order: r.sort_order,
          hint: r.hint ?? '',
          hidden: r.hidden,
        }
      : blank()
  },
  { immediate: true },
)

const editing = computed(() => !!props.row)
/** 代號的字元集與後端 `services/metrics.py::KEY_RE` 逐字相同（兩邊都擋，訊息才會一致）。 */
const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/
const keyOk = computed(() => editing.value || KEY_RE.test(draft.value.key.trim()))
const valid = computed(
  () =>
    !!draft.value.product.trim() &&
    keyOk.value &&
    !!draft.value.label.trim() &&
    draft.value.decimals != null &&
    draft.value.sort_order != null,
)

async function fail(e: unknown, fallback: string) {
  const detail =
    e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  const msg =
    detail == null
      ? fallback
      : typeof detail === 'string'
        ? detail
        : ((detail as { message?: string }).message ?? JSON.stringify(detail))
  await dialogs.notify({ title: '操作失敗', message: msg })
}

async function submit() {
  if (!valid.value || saving.value) return
  saving.value = true
  const d = draft.value
  const shared = {
    label: d.label.trim(),
    unit: d.unit.trim() || null,
    value_format: d.value_format,
    decimals: Number(d.decimals ?? 2),
    sort_order: Number(d.sort_order ?? 0),
    hint: d.hint.trim() || null,
    hidden: d.hidden,
  }
  try {
    const saved = props.row
      ? await ls.updateMetricDef(props.row.id, shared)
      : await ls.createMetricDef({ ...shared, product: d.product.trim(), key: d.key.trim() })
    emit('saved', saved)
    emit('close')
  } catch (e) {
    await fail(e, editing.value ? '更新失敗' : '建立失敗')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <UiModal
    :open="open"
    :title="editing ? `編輯指標顯示定義 — ${row?.label ?? row?.key}` : '新增指標顯示定義'"
    width="620px"
    @close="emit('close')"
  >
    <div class="form-grid two">
      <label class="field">
        <span class="field-label">產品</span>
        <select v-if="products?.length && !editing && !presetProduct" v-model="draft.product">
          <option v-for="p in products" :key="p.key" :value="p.key">{{ p.name }}（{{ p.key }}）</option>
        </select>
        <input v-else v-model="draft.product" class="mono" :disabled="editing || !!presetProduct" />
      </label>
      <label class="field">
        <span class="field-label">指標代號（key）</span>
        <input
          v-model="draft.key"
          class="mono"
          :disabled="editing || !!presetKey"
          placeholder="例：requests_total"
        />
        <p v-if="draft.key.trim() && !keyOk" class="hint bad">
          代號要小寫字母開頭，只含小寫字母／數字／底線（最長 40）。
        </p>
      </label>
      <label class="field">
        <span class="field-label">顯示名稱</span>
        <input v-model="draft.label" placeholder="後台要印的中文名" />
      </label>
      <label class="field">
        <span class="field-label">單位（純顯示後綴）</span>
        <input v-model="draft.unit" placeholder="選填，例：件／點" maxlength="16" />
      </label>
      <label class="field">
        <span class="field-label">顯示格式</span>
        <select v-model="draft.value_format">
          <option value="decimal">小數</option>
          <option value="integer">整數</option>
          <option value="percent">百分比</option>
        </select>
      </label>
      <label v-if="draft.value_format !== 'integer'" class="field">
        <span class="field-label">小數位數（0–6）</span>
        <input v-model.number="draft.decimals" type="number" min="0" max="6" />
      </label>
      <label class="field">
        <span class="field-label">排序</span>
        <input v-model.number="draft.sort_order" type="number" />
      </label>
    </div>
    <label class="field notes">
      <span class="field-label">說明（滑鼠停留時顯示）</span>
      <input v-model="draft.hint" placeholder="選填" maxlength="200" />
    </label>
    <label class="chk standalone">
      <input v-model="draft.hidden" type="checkbox" />
      隱藏（收得到但不顯示）
    </label>
    <p class="subtle">
      ★ 這份定義<strong>只影響顯示</strong>：站台回報什麼鍵由產品端決定，這裡沒有定義的鍵
      照樣會被收下來，只是在部署頁以原始英文鍵列在「未定義指標」裡。
      不想看到某個鍵請用<strong>隱藏</strong>而不是刪除——刪掉之後它下次回報又會回到未定義清單。
    </p>
    <template #foot>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="primary" :loading="saving" :disabled="!valid" @click="submit">
        {{ editing ? '儲存' : '建立' }}
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
/* 三個排版 class 與 `ProductsView.vue` 的 policies 彈窗逐字相同（那邊也是 scoped，
   共用不了；形狀要一致，改一邊記得改另一邊）。 */
.form-grid.two {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}
.field.notes {
  margin-top: var(--sp-3);
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
}
.chk.standalone {
  margin-top: var(--sp-3);
}
.hint {
  margin: 4px 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
.hint.bad {
  color: var(--danger);
}
.mono {
  font-family: ui-monospace, monospace;
}
</style>
